import { useEffect, useRef, useState } from 'react';
import { loadPdfJs } from './lazyPdfDeps';
import { THUMBNAIL_WIDTH_PX } from '../constants';

const normalizePageNumber = (value) => {
  if (typeof value === 'number' && value > 0) {
    return value;
  }
  return null;
};

const usePdfThumbnails = (pdfFile, { enabled = true } = {}) => {
  const [thumbnails, setThumbnails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef(new Map());
  const lastFileRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    if (!enabled) {
      cacheRef.current.clear();
      lastFileRef.current = null;
      setThumbnails({});
      setIsLoading(false);
      return undefined;
    }

    if (!pdfFile) {
      cacheRef.current.clear();
      lastFileRef.current = null;
      setThumbnails({});
      setIsLoading(false);
      return undefined;
    }

    if (lastFileRef.current !== pdfFile) {
      cacheRef.current = new Map();
      setThumbnails({});
      lastFileRef.current = pdfFile;
    }

    let isCancelled = false;
    let loadingTask = null;

    const renderThumbnails = async () => {
      setIsLoading(true);
      try {
        const [data, { getDocument }] = await Promise.all([
          pdfFile.arrayBuffer(),
          loadPdfJs(),
        ]);
        // Loading pdfjs is an await point, so the effect may have been torn
        // down by now; bail before creating a task nothing will destroy.
        if (isCancelled) {
          return;
        }
        loadingTask = getDocument({ data });
        const pdf = await loadingTask.promise;
        const newThumbs = {};

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (isCancelled) {
            break;
          }

          if (cacheRef.current.has(pageNumber)) {
            newThumbs[pageNumber] = cacheRef.current.get(pageNumber);
            continue;
          }

          const page = await pdf.getPage(pageNumber);
          const initialViewport = page.getViewport({ scale: 1 });
          const scale = THUMBNAIL_WIDTH_PX / initialViewport.width;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          const context = canvas.getContext('2d');
          if (!context) {
            page.cleanup();
            continue;
          }

          await page.render({ canvasContext: context, viewport }).promise;
          const dataUrl = canvas.toDataURL('image/png');

          cacheRef.current.set(pageNumber, dataUrl);
          newThumbs[pageNumber] = dataUrl;

          canvas.width = 0;
          canvas.height = 0;
          page.cleanup();
        }

        if (!isCancelled) {
          setThumbnails((prev) => ({ ...prev, ...newThumbs }));
        }

        if (typeof pdf.cleanup === 'function') {
          await pdf.cleanup();
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to render PDF thumbnails', error);
          setThumbnails({});
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    renderThumbnails();

    return () => {
      isCancelled = true;
      if (loadingTask) {
        loadingTask.destroy();
      }
    };
  }, [pdfFile, enabled]);

  const getThumbnailForPage = (pageNumber) => {
    if (!enabled) {
      return null;
    }
    const normalized = normalizePageNumber(pageNumber);
    if (!normalized) {
      return null;
    }
    return thumbnails[normalized] || null;
  };

  return { thumbnails, getThumbnailForPage, isLoading };
};

export default usePdfThumbnails;
